import React from "react";
import { FlaskConical, Recycle, Users } from "lucide-react";

const PILLARS = [
    {
        Icon: FlaskConical,
        kicker: "01 · Research as a Service",
        title: "RAAS",
        body: "Problems of medium and small-scale bio-industries, solved through rigorous, applied research — delivered as a service.",
    },
    {
        Icon: Recycle,
        kicker: "02 · Waste to Wealth",
        title: "Bio-economy",
        body: "Creating scope for a circular waste bioeconomy through innovative, commercially viable, nature-aligned products.",
    },
    {
        Icon: Users,
        kicker: "03 · Rural Empowerment",
        title: "Grassroots",
        body: "Nurturing rural talents and making them entrepreneurs through education, mentorship and hands-on skill training.",
    },
];

export const About = () => {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="section-pad relative overflow-hidden"
        >
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-moss))]/20"
                style={{ width: 620, height: 620, top: -160, left: -160 }}
            />
            <div
                aria-hidden
                className="blob bg-[hsl(var(--brand-copper))]/15"
                style={{ width: 560, height: 560, bottom: -200, right: -120 }}
            />

            <div className="container-x relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
                    <div className="lg:col-span-7">
                        <p className="overline">
                            <span className="eyebrow-line" />
                            About Yaalee
                        </p>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                            Three pillars.
                            <span className="block">
                                One <em className="not-italic text-primary">guardian</em>{" "}
                                mission.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                        Yaalee Biotech Solutions stands on three carefully chosen pillars —
                        each designed to make sustainable biotechnology an everyday reality
                        for industries, the planet and the people who power both.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {PILLARS.map(({ Icon, kicker, title, body }, i) => (
                        <article
                            key={title}
                            data-testid={`about-pillar-${i}`}
                            className="group relative rounded-[1.75rem] border border-border bg-card/60 backdrop-blur-sm p-8 md:p-10 overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:border-primary/60 hover:shadow-xl hover:shadow-primary/5"
                        >
                            {/* soft corner glow on hover */}
                            <div
                                aria-hidden
                                className="pointer-events-none absolute -top-24 -right-24 h-52 w-52 rounded-full bg-primary/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                            />

                            {/* top row: icon + index */}
                            <div className="relative flex items-start justify-between">
                                <div className="h-12 w-12 rounded-2xl bg-primary/15 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-3">
                                    0{i + 1}
                                </span>
                            </div>

                            <p className="mt-10 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                                {kicker.split(" · ")[1]}
                            </p>
                            <h3 className="mt-3 font-display text-3xl md:text-[34px] font-medium tracking-tight leading-tight">
                                {title}
                            </h3>

                            {/* thin divider */}
                            <div className="mt-6 h-px w-12 bg-border group-hover:w-20 group-hover:bg-primary transition-all duration-500" />

                            <p className="mt-6 text-[15px] leading-relaxed text-muted-foreground">
                                {body}
                            </p>
                        </article>
                    ))}
                </div>

                {/* bottom quiet caption */}
                <div className="mt-16 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                    <span className="divider-leaf">
                        Industry · Ecosystem · Community
                    </span>
                    <span>Guardian of sustainable innovation</span>
                </div>
            </div>
        </section>
    );
};

export default About;
